import type { AxiosError } from "axios";
import type { UseQueryResult } from "react-query";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { toast } from "react-toastify";

import type { IOrganization } from "../../contexts/organizations/organizationsContext";
import { AnalyticsGA } from "../../utils/AnalyticsGA";
import apiBase from "../../utils/apiBase";

export interface IQueryParams {
  pageIndex?: number;
  pageSize?: number;
  search?: string;
  status?: string;
  orderBy?: string;
  desc?: boolean;
}

export interface IUpdateOrganizationRequest {
  organizationId: string;
  name: string;
  isPublicBalancerEnabled: boolean;
}

export interface IOrganizationMembersResponse {
  items: IMember[];
  totalCount: number;
  filteredCount: number;
  pageIndex: number;
  pageSize: number;
  search: string;
}

export interface IInviteRevoke {
  organizationId: string;
  invitationId: string;
}

export interface IDeleteUserRequest {
  organizationId: string;
  userId: string;
}

interface IMember {
  firstName?: string;
  lastName?: string;
  id: string;
  email: string;
  touchcastTenantId?: string;
  organizations?: IOrganization[];
  joinedDate?: string;
  role?: string;
}

export interface InviteUserRequest {
  organizationId: string;
  invitee: {
    email: string;
    firstName: string;
    lastName: string;
  };
  inviter: {
    id: string;
  };
  roles: string[];
}

export interface IUpdateOrganizationResponse {
  owner: {
    id: string;
    email: string;
  };
  userSettings: {
    twoFactorAuthenticationRequired: boolean;
  };
  restrictions: {
    userRestrictions: {
      maxUsersQuantity: number;
    };
  };
  id: string;
  name: string;
  created: number;
  isPublicBalancerEnabled: boolean;
}

export const fetchOrganizationMembers = async (
  organizationId: string,
  params: IQueryParams,
): Promise<IOrganizationMembersResponse> => {
  return await apiBase.get(`organizations/${organizationId}/users`, {
    params,
  });
};

const fetchOrganizationInvitations = async (
  organizationId: string,
  params: IQueryParams,
): Promise<IOrganizationMembersResponse> => {
  return await apiBase.get(`organizations/${organizationId}/invitations`, {
    params,
    headers: {
      "X-API-Version": "2.0",
    },
  });
};

export const fetchOrganizations = async (): Promise<IOrganization[]> => {
  return await apiBase.get(`/organizations/my`);
};

const updateOrganization = async (
  data: IUpdateOrganizationRequest,
): Promise<IUpdateOrganizationResponse> => {
  const { organizationId, name, isPublicBalancerEnabled } = data;
  return await apiBase.patch(`organizations/${organizationId}`, {
    name,
    isPublicBalancerEnabled,
  });
};

export const inviteUser = async (data: InviteUserRequest): Promise<any> => {
  return await apiBase.post(
    `organizations/${data.organizationId}/users/invite`,
    {
      invitee: data.invitee,
      inviter: data.inviter,
      roles: data.roles,
    },
    {
      headers: {
        "X-API-Version": "2.0",
      },
    },
  );
};

const invitationRevoke = async (data: IInviteRevoke): Promise<any> => {
  return await apiBase.post(
    `/organizations/${data.organizationId}/invitations/${data.invitationId}/revoke`,
    null,
    {
      headers: {
        "X-API-Version": "2.0",
      },
    },
  );
};

const invitationRemind = async (data: IInviteRevoke): Promise<any> => {
  return await apiBase.post(
    `/organizations/${data.organizationId}/invitations/${data.invitationId}/remind`,
    null,
    {
      headers: {
        "X-API-Version": "2.0",
      },
    },
  );
};

export const useOrganizationMembers = (
  organizationId: string,
  params: IQueryParams,
): UseQueryResult<IOrganizationMembersResponse> => {
  return useQuery(["organizationMembers", organizationId, params], () =>
    fetchOrganizationMembers(organizationId, params),
  );
};

export const useOrganizationInvitations = (
  organizationId: string,
  params: IQueryParams,
): UseQueryResult<IOrganizationMembersResponse> => {
  return useQuery(["organizationInvitations", organizationId, params], () =>
    fetchOrganizationInvitations(organizationId, params),
  );
};

export const useOrganizations = () => {
  return useQuery(["organizations"], () => fetchOrganizations(), {
    enabled: false,
  });
};

export const useUpdateOrganization = () => {
  const queryClient = useQueryClient();
  return useMutation(updateOrganization, {
    onSuccess: () => {
      queryClient.invalidateQueries("organizations").catch(console.error);
      toast.success("Organization updated successfully");
    },
  });
};

export const useUpdateOrganizationLink = () => {
  return useMutation(updateOrganization, {
    onSuccess: () => {
      toast.success("Settings updated successfully");
    },
    onError: () => {
      toast.error("Something went wrong. Please try again later");
    },
  });
};

export const useInviteUser = () => {
  const queryClient = useQueryClient();
  return useMutation<any, AxiosError, InviteUserRequest>(inviteUser, {
    onSuccess: () => {
      queryClient
        .invalidateQueries("organizationInvitations")
        .catch(console.error);
      toast.success("Member added and notified successfully");
      AnalyticsGA.trackEvent("Member", "Invite", "Member invited successfully");
    },
  });
};

const deleteUser = async ({
  organizationId,
  userId,
}: IDeleteUserRequest): Promise<{ status: string }> => {
  return await apiBase.delete(
    `/organizations/${organizationId}/users/${userId}`,
  );
};

export const useDeleteUser = () => {
  const queryClient = useQueryClient();

  return useMutation(deleteUser, {
    onSuccess: () => {
      queryClient.invalidateQueries("organizationMembers").catch(console.error);
      toast.success("Member deleted successfully");
      AnalyticsGA.trackEvent("Member", "Delete", "Member deleted successfully");
    },
  });
};

export const useInvitationRevoke = () => {
  const queryClient = useQueryClient();
  return useMutation<any, AxiosError, IInviteRevoke>(invitationRevoke, {
    onSuccess: () => {
      queryClient
        .invalidateQueries("organizationInvitations")
        .catch(console.error);
      toast.success("Member revoked successfully");
      AnalyticsGA.trackEvent("Member", "Revoke", "Member revoked successfully");
    },
    onError: (e: AxiosError) => {
      toast.error(
        e.response?.data[0]?.description ||
          "Something went wrong. Please try again later",
      );
    },
  });
};

export const useInvitationRemind = () => {
  const queryClient = useQueryClient();
  return useMutation<any, AxiosError, IInviteRevoke>(invitationRemind, {
    onSuccess: () => {
      queryClient
        .invalidateQueries("organizationInvitations")
        .catch(console.error);
      toast.success("Notification sent successfully");
      AnalyticsGA.trackEvent("Member", "Remind", "Member Remind successfully");
    },
    onError: (e: AxiosError) => {
      toast.error(
        e.response?.data[0]?.description ||
          "Something went wrong. Please try again later",
      );
    },
  });
};
